/**
 * TournamentModal - Interactive Mini-Tournament Bracket & Manager Modal
 * Renders full 4 to 8 player single & double elimination brackets,
 * setup wizard, live match launcher, manual score recorder for physical pub boards,
 * and celebratory champion crowning ceremony.
 */

import { TournamentManager, TOURNAMENT_FORMATS, TOURNAMENT_SERIES_MODES, DEFAULT_AVATARS } from '../series/TournamentManager.js';
import { CelebrationFX } from './CelebrationFX.js';
import { sanitizeUserInput } from '../utils/Sanitizer.js';

export class TournamentModal {
  static MODAL_ID = 'crazyTournamentModal';
  static OVERLAY_ID = 'crazyTournamentOverlay';

  /**
   * Opens the Tournament modal. If an active tournament exists, shows the bracket;
   * otherwise opens the setup wizard.
   * @param {Object} [context]
   */
  static open(context = {}) {
    if (typeof document === 'undefined') return;

    // Mini-Tournament mode is exclusive to Crazy Cribbage
    const brand = context.brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy');
    if (brand !== 'crazy') {
      if (typeof window !== 'undefined' && typeof window.showScoreToast === 'function') {
        window.showScoreToast('Mini-Tournament is only available on Crazy Cribbage.', 3000);
      }
      return;
    }

    let overlay = document.getElementById(TournamentModal.OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = TournamentModal.OVERLAY_ID;
      overlay.className = 'tournament-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Mini-Tournament Bracket Manager');
      document.body.appendChild(overlay);
    }

    overlay.style.display = 'flex';
    TournamentModal.render(context);
  }

  /**
   * Closes the tournament modal.
   */
  static close() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById(TournamentModal.OVERLAY_ID);
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Main render dispatcher for tournament modal.
   * @param {Object} [context]
   */
  static render(context = {}) {
    if (typeof document === 'undefined') return;

    // Mini-Tournament mode is exclusive to Crazy Cribbage
    const brand = context.brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy');
    if (brand !== 'crazy') {
      TournamentModal.close();
      return;
    }

    const overlay = document.getElementById(TournamentModal.OVERLAY_ID);
    if (!overlay) return;

    const state = TournamentManager.getTournamentState();
    if (!state || context.forceSetup) {
      TournamentModal.renderSetup(overlay, context);
    } else {
      TournamentModal.renderBracket(overlay, state, context);
    }
  }

  /**
   * Helper to serialize current tournament setup form data.
   * @param {HTMLElement} overlay
   * @returns {Object|null}
   */
  static getSetupFormData(overlay) {
    if (!overlay) return null;
    const rawTitle = overlay.querySelector('#tourneyTitleInput')?.value || 'Table Championship';
    const titleVal = sanitizeUserInput(rawTitle) || 'Table Championship';
    const formatVal = overlay.querySelector('#tourneyFormatSel')?.value || 'single';
    const seriesVal = overlay.querySelector('#tourneySeriesSel')?.value || 'bo3';
    const activeCountBtn = overlay.querySelector('.tourney-count-btn.active');
    const countVal = activeCountBtn ? Number(activeCountBtn.getAttribute('data-count')) : 4;

    const rosterRows = overlay.querySelectorAll('.tourney-roster-row');
    const players = [];
    rosterRows.forEach((row, idx) => {
      const seed = idx + 1;
      const rawName = row.querySelector('.tourney-player-name')?.value || `Player ${seed}`;
      const nameVal = sanitizeUserInput(rawName) || `Player ${seed}`;
      const typeVal = row.querySelector('.tourney-player-type')?.value || 'cpu:shark';
      players.push({
        seed,
        name: nameVal,
        typeVal
      });
    });

    return {
      title: titleVal,
      format: formatVal,
      seriesMode: seriesVal,
      playerCount: countVal,
      players
    };
  }

  /**
   * Persists current setup selections to storage so user settings are remembered.
   * @param {HTMLElement} overlay
   */
  static autoSaveSetup(overlay) {
    const data = TournamentModal.getSetupFormData(overlay);
    if (!data) return;
    const existing = TournamentManager.getSavedSetupSettings() || {};
    const mergedPlayers = Array.isArray(existing.players) ? [...existing.players] : [];
    data.players.forEach(p => {
      const idx = mergedPlayers.findIndex(ep => ep.seed === p.seed);
      if (idx >= 0) {
        mergedPlayers[idx] = { ...mergedPlayers[idx], ...p };
      } else {
        mergedPlayers.push(p);
      }
    });
    mergedPlayers.sort((a, b) => a.seed - b.seed);
    data.players = mergedPlayers;
    TournamentManager.saveSetupSettings(data);
  }

  /**
   * Renders the tournament setup view.
   * @param {HTMLElement} overlay
   * @param {Object} context
   */
  static renderSetup(overlay, context = {}) {
    const brand = context.brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy');
    if (brand !== 'crazy') {
      TournamentModal.close();
      return;
    }

    const saved = TournamentManager.getSavedSetupSettings() || {};
    const activeTourney = TournamentManager.getTournamentState();
    const canResumeBracket = Boolean(activeTourney && !activeTourney.isComplete);

    let playerCount = context.playerCount || saved.playerCount || 4;
    let format = context.format || saved.format || TOURNAMENT_FORMATS.SINGLE_ELIM;
    let seriesMode = context.seriesMode || saved.seriesMode || TOURNAMENT_SERIES_MODES.BO3;
    let tourneyTitle = context.title || saved.title || 'Table Championship';
    let savedPlayers = context.players || saved.players || [];

    // Get online peers if host
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const onlineClients = [];
    if (net && net.clients) {
      for (const [peerId, client] of net.clients.entries()) {
        if (client.conn && client.conn.open) {
          onlineClients.push({
            peerId,
            name: client.name || `Guest (${client.seatId || 'p2'})`,
            seatId: client.seatId
          });
        }
      }
    }

    const defaultNames = ['Player 1 (You)', 'The Shark', 'The Gambler', 'The Turtler', 'Pub Master Dave', 'Lucky Sarah', 'Peggin Pete', 'Cribbage Chris'];
    const defaultTypes = ['human', 'cpu', 'cpu', 'cpu', 'human', 'human', 'cpu', 'cpu'];
    const defaultCpuProfiles = ['shark', 'shark', 'gambler', 'turtler', 'gambler', 'shark', 'gambler', 'turtler'];

    const renderRosterRows = (count) => {
      let rows = '';
      for (let i = 0; i < count; i++) {
        const seed = i + 1;
        const avatar = DEFAULT_AVATARS[i % DEFAULT_AVATARS.length];
        const savedP = savedPlayers.find(p => p.seed === seed) || savedPlayers[i] || null;
        const name = (savedP && savedP.name) ? savedP.name : (defaultNames[i] || `Player ${seed}`);

        let typeVal = savedP?.typeVal;
        if (!typeVal && savedP) {
          if (savedP.type === 'human') typeVal = 'human';
          else if (savedP.type === 'online') typeVal = `online:${savedP.peerId}`;
          else if (savedP.type === 'cpu') typeVal = `cpu:${savedP.cpuProfile || 'shark'}`;
        }
        if (!typeVal) {
          const defType = defaultTypes[i] || 'cpu';
          const defProfile = defaultCpuProfiles[i] || 'shark';
          typeVal = defType === 'human' ? 'human' : `cpu:${defProfile}`;
        }

        let onlineOption = '';
        if (typeVal.startsWith('online:')) {
          const pId = typeVal.split(':')[1];
          const hasClient = onlineClients.some(c => c.peerId === pId);
          if (!hasClient) {
            onlineOption = `<option value="${typeVal}" selected>🌐 Online Guest (${pId.substring(0, 6)}...)</option>`;
          }
        }

        rows += `
          <div class="tourney-roster-row" data-seed="${seed}" style="display:flex;align-items:center;gap:10px;margin-bottom:8px;padding:8px 12px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.1);border-radius:8px;">
            <div style="font-weight:900;color:#f0c64a;width:24px;text-align:center;">#${seed}</div>
            <div class="tourney-avatar-wrap" style="font-size:1.25rem;">
              <span id="tourneyAvatar_${seed}">${avatar}</span>
            </div>
            <input type="text" id="tourneyName_${seed}" class="tourney-player-name" value="${name.replace(/"/g, '&quot;')}" maxlength="24" pattern="[a-zA-Z0-9]+"
                   style="flex:1;background:rgba(0,0,0,0.4);border:1px solid rgba(240,198,74,0.3);border-radius:6px;padding:6px 10px;color:#f4ead4;font-size:0.92rem;font-weight:700;" placeholder="Player name">
            <select id="tourneyType_${seed}" class="tourney-player-type"
                    style="background:rgba(20,15,10,0.9);border:1px solid rgba(240,198,74,0.3);border-radius:6px;padding:6px 10px;color:#f0c64a;font-size:0.85rem;font-weight:700;cursor:pointer;">
              <option value="human" ${typeVal === 'human' ? 'selected' : ''}>👤 Human (Local)</option>
              <option value="cpu:shark" ${typeVal === 'cpu:shark' ? 'selected' : ''}>🦈 Expert — The Shark</option>
              <option value="cpu:master" ${typeVal === 'cpu:master' ? 'selected' : ''}>👑 Expert — The Grandmaster</option>
              <option value="cpu:turtler" ${typeVal === 'cpu:turtler' ? 'selected' : ''}>🐢 Advanced — The Turtler</option>
              <option value="cpu:maverick" ${typeVal === 'cpu:maverick' ? 'selected' : ''}>⚡ Advanced — The Maverick</option>
              <option value="cpu:gambler" ${typeVal === 'cpu:gambler' ? 'selected' : ''}>🎲 Intermediate — The Gambler</option>
              <option value="cpu:regular" ${typeVal === 'cpu:regular' ? 'selected' : ''}>🦊 Intermediate — The Regular</option>
              <option value="cpu:rookie" ${typeVal === 'cpu:rookie' ? 'selected' : ''}>🌱 Beginner — The Rookie</option>
              <option value="cpu:casual" ${typeVal === 'cpu:casual' ? 'selected' : ''}>☕ Beginner — The Casual</option>
              ${onlineOption}
              ${onlineClients.map(c => `<option value="online:${c.peerId}" ${typeVal === `online:${c.peerId}` ? 'selected' : ''}>🌐 Online: ${c.name}</option>`).join('')}
            </select>
          </div>
        `;
      }
      return rows;
    };

    overlay.innerHTML = `
      <div class="tournament-modal-card tourney-setup-card" style="max-width:680px;width:95%;max-height:92vh;background:linear-gradient(135deg,rgba(26,18,8,0.98),rgba(15,10,5,0.99));border:2px solid #f0c64a;border-radius:14px;box-shadow:0 15px 45px rgba(0,0,0,0.85),0 0 25px rgba(240,198,74,0.25);overflow:hidden;display:flex;flex-direction:column;color:#f4ead4;">
        <div class="tourney-modal-header" style="display:flex;justify-content:space-between;align-items:center;padding:16px 20px;border-bottom:1px solid rgba(240,198,74,0.3);background:rgba(0,0,0,0.35);">
          <div style="display:flex;align-items:center;gap:10px;">
            <span style="font-size:1.6rem;">🏆</span>
            <div>
              <div style="font-size:1.15rem;font-weight:900;color:#f0c64a;letter-spacing:0.04em;">MINI-TOURNAMENT SETUP</div>
              <div style="font-size:0.78rem;color:#d6d3d1;">4 to 8 Player Bracket Manager for Game Nights & Online Tables</div>
            </div>
          </div>
          <button type="button" class="btn-ghost tourney-close-btn" style="background:transparent;border:none;color:#a8a29e;font-size:1.5rem;cursor:pointer;padding:4px 8px;line-height:1;">✕</button>
        </div>

        <div class="tourney-modal-body" style="padding:18px 20px;overflow-y:auto;flex:1;">
          <div style="margin-bottom:14px;">
            <label style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;color:#f0c64a;margin-bottom:5px;letter-spacing:0.05em;">Tournament Title</label>
            <input type="text" id="tourneyTitleInput" value="${tourneyTitle}" style="width:100%;box-sizing:border-box;background:rgba(0,0,0,0.45);border:1.5px solid rgba(240,198,74,0.4);border-radius:8px;padding:9px 12px;color:#fff;font-size:1rem;font-weight:800;">
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
            <div>
              <label style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;color:#f0c64a;margin-bottom:5px;letter-spacing:0.05em;">Bracket Format</label>
              <select id="tourneyFormatSel" style="width:100%;background:rgba(20,15,10,0.95);border:1.5px solid rgba(240,198,74,0.4);border-radius:8px;padding:9px 12px;color:#f0c64a;font-size:0.95rem;font-weight:800;cursor:pointer;">
                <option value="single" ${format === 'single' ? 'selected' : ''}>Single Elimination</option>
                <option value="double" ${format === 'double' ? 'selected' : ''}>Double Elimination</option>
              </select>
            </div>
            <div>
              <label style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;color:#f0c64a;margin-bottom:5px;letter-spacing:0.05em;">Match Series Length</label>
              <select id="tourneySeriesSel" style="width:100%;background:rgba(20,15,10,0.95);border:1.5px solid rgba(240,198,74,0.4);border-radius:8px;padding:9px 12px;color:#f0c64a;font-size:0.95rem;font-weight:800;cursor:pointer;">
                <option value="bo1" ${seriesMode === 'bo1' ? 'selected' : ''}>Best of 1 (Single Game)</option>
                <option value="bo3" ${seriesMode === 'bo3' ? 'selected' : ''}>Best of 3 (First to 2 Wins)</option>
                <option value="bo5" ${seriesMode === 'bo5' ? 'selected' : ''}>Best of 5 (First to 3 Wins)</option>
              </select>
            </div>
          </div>

          <div style="margin-bottom:16px;">
            <label style="display:block;font-size:0.8rem;font-weight:800;text-transform:uppercase;color:#f0c64a;margin-bottom:8px;letter-spacing:0.05em;">Number of Players (4 to 8)</label>
            <div style="display:flex;gap:8px;">
              ${[4, 5, 6, 7, 8].map(n => `
                <button type="button" class="tourney-count-btn ${n === playerCount ? 'active' : ''}" data-count="${n}"
                        style="flex:1;padding:8px 0;font-weight:900;font-size:1rem;border-radius:8px;cursor:pointer;transition:all 0.15s ease;border:1.5px solid ${n === playerCount ? '#f0c64a' : 'rgba(255,255,255,0.15)'};background:${n === playerCount ? 'rgba(240,198,74,0.25)' : 'rgba(0,0,0,0.3)'};color:${n === playerCount ? '#f0c64a' : '#d6d3d1'};">
                  ${n}
                </button>
              `).join('')}
            </div>
          </div>

          <div style="margin-bottom:14px;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
              <label style="font-size:0.8rem;font-weight:800;text-transform:uppercase;color:#f0c64a;letter-spacing:0.05em;">Bracket Seeds & Participants</label>
              <div style="display:flex;gap:6px;">
                <button type="button" id="btnShuffleSeeds" class="btn-ghost" style="padding:4px 8px;font-size:0.75rem;border:1px solid rgba(240,198,74,0.4);border-radius:6px;color:#f0c64a;cursor:pointer;">🎲 Shuffle Seeds</button>
                <button type="button" id="btnPresetBots" class="btn-ghost" style="padding:4px 8px;font-size:0.75rem;border:1px solid rgba(240,198,74,0.4);border-radius:6px;color:#f0c64a;cursor:pointer;">🤖 Fill Bots</button>
              </div>
            </div>
            <div id="tourneyRosterWrap">
              ${renderRosterRows(playerCount)}
            </div>
          </div>
        </div>

        <div class="tourney-modal-footer" style="padding:14px 20px;border-top:1px solid rgba(240,198,74,0.3);background:rgba(0,0,0,0.4);display:flex;justify-content:flex-end;gap:12px;">
          ${canResumeBracket ? `
            <button type="button" class="btn-ghost" id="btnCancelSetup" style="padding:10px 18px;border:1px solid rgba(255,255,255,0.2);border-radius:8px;color:#d6d3d1;font-weight:700;cursor:pointer;">Resume Active Bracket</button>
          ` : ''}
          <button type="button" class="btn-gold" id="btnGenerateTournament" style="padding:11px 24px;border-radius:8px;font-weight:900;font-size:1rem;cursor:pointer;background:linear-gradient(135deg,#f0c64a,#ca8a04);color:#1a1208;border:none;box-shadow:0 4px 15px rgba(240,198,74,0.4);">
            🏆 Launch Tournament
          </button>
        </div>
      </div>
    `;

    overlay.querySelectorAll('.tourney-close-btn').forEach(btn => {
      btn.addEventListener('click', () => TournamentModal.close());
    });

    // Wire player count buttons
    const countBtns = overlay.querySelectorAll('.tourney-count-btn');
    countBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const count = Number(btn.getAttribute('data-count')) || 4;
        const currentData = TournamentModal.getSetupFormData(overlay);
        if (currentData) {
          currentData.playerCount = count;
          TournamentManager.saveSetupSettings(currentData);
        }
        TournamentModal.renderSetup(overlay, {
          ...context,
          playerCount: count,
          title: currentData?.title || tourneyTitle,
          format: currentData?.format || format,
          seriesMode: currentData?.seriesMode || seriesMode,
          players: currentData?.players || savedPlayers
        });
      });
    });

    // Wire auto-save events for inputs and selects
    const titleInput = overlay.querySelector('#tourneyTitleInput');
    if (titleInput) {
      titleInput.addEventListener('input', () => TournamentModal.autoSaveSetup(overlay));
      titleInput.addEventListener('change', () => TournamentModal.autoSaveSetup(overlay));
    }
    const formatSel = overlay.querySelector('#tourneyFormatSel');
    if (formatSel) {
      formatSel.addEventListener('change', () => TournamentModal.autoSaveSetup(overlay));
    }
    const seriesSel = overlay.querySelector('#tourneySeriesSel');
    if (seriesSel) {
      seriesSel.addEventListener('change', () => TournamentModal.autoSaveSetup(overlay));
    }

    const nameInputs = overlay.querySelectorAll('.tourney-player-name');
    nameInputs.forEach(inp => {
      inp.addEventListener('input', () => {
        inp.value = inp.value.replace(/[^a-zA-Z0-9]/g, '');
        TournamentModal.autoSaveSetup(overlay);
      });
      inp.addEventListener('change', () => TournamentModal.autoSaveSetup(overlay));
    });

    const typeSelects = overlay.querySelectorAll('.tourney-player-type');
    typeSelects.forEach(sel => {
      sel.addEventListener('change', () => TournamentModal.autoSaveSetup(overlay));
    });

    // Wire shuffle seeds
    const shuffleBtn = overlay.querySelector('#btnShuffleSeeds');
    if (shuffleBtn) {
      shuffleBtn.addEventListener('click', () => {
        const curNameInputs = overlay.querySelectorAll('.tourney-player-name');
        const names = Array.from(curNameInputs).map(inp => inp.value);
        for (let i = names.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [names[i], names[j]] = [names[j], names[i]];
        }
        curNameInputs.forEach((inp, idx) => {
          inp.value = names[idx] || `Player ${idx + 1}`;
        });
        TournamentModal.autoSaveSetup(overlay);
      });
    }

    // Wire preset bots
    const presetBotsBtn = overlay.querySelector('#btnPresetBots');
    if (presetBotsBtn) {
      presetBotsBtn.addEventListener('click', () => {
        const curTypeSelects = overlay.querySelectorAll('.tourney-player-type');
        const profiles = ['shark', 'master', 'turtler', 'maverick', 'gambler', 'regular', 'rookie', 'casual'];
        curTypeSelects.forEach((sel, idx) => {
          if (idx === 0) sel.value = 'human';
          else sel.value = `cpu:${profiles[(idx - 1) % profiles.length]}`;
        });
        TournamentModal.autoSaveSetup(overlay);
      });
    }

    // Wire resume button if existing
    const cancelBtn = overlay.querySelector('#btnCancelSetup');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        TournamentModal.render({ forceSetup: false });
      });
    }

    // Wire Launch Tournament button
    const launchBtn = overlay.querySelector('#btnGenerateTournament');
    if (launchBtn) {
      launchBtn.addEventListener('click', () => {
        TournamentModal.autoSaveSetup(overlay);
        const titleVal = (overlay.querySelector('#tourneyTitleInput')?.value || 'Table Championship').trim();
        const formatVal = overlay.querySelector('#tourneyFormatSel')?.value || 'single';
        const seriesVal = overlay.querySelector('#tourneySeriesSel')?.value || 'bo3';

        const rosterRows = overlay.querySelectorAll('.tourney-roster-row');
        const players = [];

        rosterRows.forEach((row, idx) => {
          const seed = idx + 1;
          const raw = row.querySelector('.tourney-player-name')?.value || '';
          const nameVal = raw.replace(/[^a-zA-Z0-9]/g, '').trim() || `Player${seed}`;
          const typeVal = row.querySelector('.tourney-player-type')?.value || 'cpu:shark';
          const avatar = DEFAULT_AVATARS[idx % DEFAULT_AVATARS.length];

          let type = 'cpu';
          let cpuProfile = 'shark';
          let peerId = null;

          if (typeVal === 'human') {
            type = 'human';
          } else if (typeVal.startsWith('cpu:')) {
            type = 'cpu';
            cpuProfile = typeVal.split(':')[1] || 'shark';
          } else if (typeVal.startsWith('online:')) {
            type = 'online';
            peerId = typeVal.split(':')[1];
          }

          players.push({
            id: `p${seed}`,
            name: nameVal,
            type,
            cpuProfile,
            avatar,
            seed,
            peerId
          });
        });

        const brand = typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy';
        const targetPoints = (typeof window !== 'undefined' && window.settings && window.settings.target) ? Number(window.settings.target) : 121;

        const newTourney = TournamentManager.createTournament({
          title: titleVal,
          format: formatVal,
          seriesMode: seriesVal,
          playerCount: players.length,
          players,
          brand,
          targetPoints
        });

        // Broadcast to multiplayer peers if host
        TournamentModal._broadcastTournament(newTourney, context);

        TournamentModal.render({ forceSetup: false });
      });
    }
  }

  /**
   * Renders the active bracket tree, match progression, and champion podium.
   * @param {HTMLElement} overlay
   * @param {Object} state
   * @param {Object} context
   */
  static renderBracket(overlay, state, context = {}) {
    const isCompleted = state.isComplete;
    const champ = state.champion ? TournamentManager.getPlayer(state, state.champion) : null;
    const formatLabel = state.format === 'double' ? 'Double Elimination' : 'Single Elimination';
    const seriesLabel = state.seriesMode.toUpperCase();

    // Group matches by bracket and round
    const winnersRounds = {};
    const losersRounds = {};
    const finalsMatches = [];

    for (const match of Object.values(state.matches)) {
      if (match.bracket === 'finals' || match.id === 'm_finals' || match.id === 'm_grand_finals') {
        finalsMatches.push(match);
      } else if (match.bracket === 'losers') {
        losersRounds[match.round] = losersRounds[match.round] || [];
        losersRounds[match.round].push(match);
      } else {
        winnersRounds[match.round] = winnersRounds[match.round] || [];
        winnersRounds[match.round].push(match);
      }
    }

    const renderMatchCard = (match) => {
      const p1 = TournamentManager.getPlayer(state, match.player1Id);
      const p2 = TournamentManager.getPlayer(state, match.player2Id);
      const isBye = match.status === 'bye';
      const isReady = match.status === 'ready';
      const isInProg = match.status === 'in_progress';
      const isDone = match.status === 'completed';
      const isActive = state.activeMatchId === match.id;

      const p1Wins = (match.wins && match.player1Id && match.wins[match.player1Id]) || 0;
      const p2Wins = (match.wins && match.player2Id && match.wins[match.player2Id]) || 0;
      const p1Won = isDone && match.winnerId === match.player1Id;
      const p2Won = isDone && match.winnerId === match.player2Id;

      const p1Name = p1 ? p1.name : (isBye ? 'BYE' : 'TBD');
      const p2Name = p2 ? p2.name : (isBye ? 'BYE' : 'TBD');
      const p1Avatar = p1 ? p1.avatar : '❓';
      const p2Avatar = p2 ? p2.avatar : '❓';

      let borderStyle = '1px solid rgba(255,255,255,0.12)';
      if (isActive && !isDone) borderStyle = '2px solid #f0c64a; box-shadow: 0 0 16px rgba(240,198,74,0.45);';
      else if (isDone) borderStyle = '1px solid rgba(52,211,153,0.35);';

      let statusBadge = '';
      if (isDone) statusBadge = `<span style="font-size:0.68rem;font-weight:800;background:rgba(52,211,153,0.2);color:#34d399;padding:2px 6px;border-radius:4px;border:1px solid rgba(52,211,153,0.4);">COMPLETED</span>`;
      else if (isInProg) statusBadge = `<span style="font-size:0.68rem;font-weight:800;background:rgba(240,198,74,0.2);color:#f0c64a;padding:2px 6px;border-radius:4px;border:1px solid rgba(240,198,74,0.4);">LIVE</span>`;
      else if (isReady) statusBadge = `<span style="font-size:0.68rem;font-weight:800;background:rgba(59,130,246,0.2);color:#60a5fa;padding:2px 6px;border-radius:4px;border:1px solid rgba(59,130,246,0.4);">READY</span>`;
      else if (isBye) statusBadge = `<span style="font-size:0.68rem;font-weight:800;background:rgba(168,85,247,0.2);color:#c084fc;padding:2px 6px;border-radius:4px;border:1px solid rgba(168,85,247,0.4);">BYE</span>`;
      else statusBadge = `<span style="font-size:0.68rem;color:#78716c;">WAITING</span>`;

      // Match actions
      let actionsHtml = '';
      if ((isReady || isInProg) && p1 && p2) {
        const canSimulate = p1.type === 'cpu' && p2.type === 'cpu';
        actionsHtml = `
          <div class="tourney-match-actions" style="display:flex;gap:6px;margin-top:8px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.08);">
            <button type="button" class="btn-play-match btn-gold" data-match-id="${match.id}"
                    style="flex:2;padding:5px 8px;font-size:0.78rem;font-weight:800;border-radius:6px;cursor:pointer;background:linear-gradient(135deg,#f0c64a,#ca8a04);color:#1a1208;border:none;">
              ▶ Play Match
            </button>
            <button type="button" class="btn-manual-score btn-ghost" data-match-id="${match.id}" title="Enter match score manually (for pub game nights)"
                    style="flex:1;padding:5px 6px;font-size:0.75rem;border:1px solid rgba(240,198,74,0.4);border-radius:6px;color:#f0c64a;background:rgba(0,0,0,0.3);cursor:pointer;">
              ✏ Record
            </button>
            ${canSimulate ? `
              <button type="button" class="btn-sim-match btn-ghost" data-match-id="${match.id}" title="Simulate CPU vs CPU match"
                      style="flex:1;padding:5px 6px;font-size:0.75rem;border:1px solid rgba(168,85,247,0.4);border-radius:6px;color:#c084fc;background:rgba(0,0,0,0.3);cursor:pointer;">
                ⚡ Sim
              </button>
            ` : ''}
          </div>
        `;
      }

      return `
        <div class="tourney-match-node ${isActive ? 'active-match' : ''}" data-match-id="${match.id}"
             style="background:rgba(20,15,10,0.92);border-radius:10px;padding:10px 12px;margin-bottom:12px;width:230px;box-sizing:border-box;border:${borderStyle};transition:all 0.2s ease;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-size:0.72rem;font-weight:800;color:#d6d3d1;text-transform:uppercase;letter-spacing:0.04em;">${match.roundLabel || 'Match'}</span>
            ${statusBadge}
          </div>

          <div class="tourney-slot ${p1Won ? 'winner-slot' : ''}"
               style="display:flex;align-items:center;justify-content:space-between;padding:5px 8px;border-radius:6px;background:${p1Won ? 'rgba(52,211,153,0.18)' : 'rgba(255,255,255,0.03)'};margin-bottom:4px;border:${p1Won ? '1px solid rgba(52,211,153,0.35)' : '1px solid transparent'};">
            <div style="display:flex;align-items:center;gap:6px;overflow:hidden;flex:1;">
              <span style="font-size:0.95rem;">${p1Avatar}</span>
              <span style="font-size:0.86rem;font-weight:700;color:${p1Won ? '#34d399' : (p1 ? '#f4ead4' : '#78716c')};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                ${p1 ? p1.name : (isBye ? 'BYE' : 'TBD')}
              </span>
            </div>
            <div style="font-size:0.95rem;font-weight:900;color:${p1Won ? '#34d399' : '#f0c64a'};margin-left:8px;">
              ${p1 ? p1Wins : '-'}
            </div>
          </div>

          <div class="tourney-slot ${p2Won ? 'winner-slot' : ''}"
               style="display:flex;align-items:center;justify-content:space-between;padding:5px 8px;border-radius:6px;background:${p2Won ? 'rgba(52,211,153,0.18)' : 'rgba(255,255,255,0.03)'};border:${p2Won ? '1px solid rgba(52,211,153,0.35)' : '1px solid transparent'};">
            <div style="display:flex;align-items:center;gap:6px;overflow:hidden;flex:1;">
              <span style="font-size:0.95rem;">${p2Avatar}</span>
              <span style="font-size:0.86rem;font-weight:700;color:${p2Won ? '#34d399' : (p2 ? '#f4ead4' : '#78716c')};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                ${p2 ? p2.name : (isBye ? 'BYE' : 'TBD')}
              </span>
            </div>
            <div style="font-size:0.95rem;font-weight:900;color:${p2Won ? '#34d399' : '#f0c64a'};margin-left:8px;">
              ${p2 ? p2Wins : '-'}
            </div>
          </div>

          ${actionsHtml}
        </div>
      `;
    };

    // Render tree columns
    const renderRoundColumn = (roundMatches, roundLabel) => `
      <div class="tourney-bracket-col" style="display:flex;flex-direction:column;justify-content:space-around;min-width:240px;margin-right:16px;">
        <div style="text-align:center;font-size:0.82rem;font-weight:800;color:#f0c64a;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:12px;padding:4px 0;background:rgba(0,0,0,0.3);border-radius:6px;">
          ${roundLabel}
        </div>
        <div style="display:flex;flex-direction:column;justify-content:space-around;flex:1;">
          ${roundMatches.map(m => renderMatchCard(m)).join('')}
        </div>
      </div>
    `;

    // Render podium if completed
    let podiumHtml = '';
    if (isCompleted && champ) {
      const p1st = champ;
      const p2nd = state.podium?.second ? TournamentManager.getPlayer(state, state.podium.second) : null;
      const p3rd = state.podium?.third ? TournamentManager.getPlayer(state, state.podium.third) : null;

      podiumHtml = `
        <div class="tourney-podium-card" style="margin-bottom:16px;padding:16px 20px;background:linear-gradient(135deg,rgba(240,198,74,0.18),rgba(0,0,0,0.6));border:2px solid #f0c64a;border-radius:12px;text-align:center;box-shadow:0 8px 30px rgba(240,198,74,0.35);">
          <div style="font-size:1.8rem;line-height:1;margin-bottom:4px;">👑 🏆 👑</div>
          <div style="font-size:1.35rem;font-weight:900;color:#f0c64a;text-transform:uppercase;letter-spacing:0.06em;">
            TABLE CHAMPION CROWNED!
          </div>
          <div style="font-size:1.25rem;font-weight:900;color:#ffffff;margin:8px 0;">
            ${p1st.avatar} ${p1st.name}
          </div>
          <div style="display:flex;justify-content:center;gap:20px;margin-top:10px;flex-wrap:wrap;">
            ${p2nd ? `<div style="font-size:0.88rem;color:#e2e8f0;">🥈 <strong>Runner-Up:</strong> ${p2nd.name}</div>` : ''}
            ${p3rd ? `<div style="font-size:0.88rem;color:#fbd38d;">🥉 <strong>3rd Place:</strong> ${p3rd.name}</div>` : ''}
          </div>
        </div>
      `;
    }

    overlay.innerHTML = `
      <div class="tournament-modal-card" style="max-width:1100px;width:96%;max-height:94vh;background:linear-gradient(135deg,rgba(26,18,8,0.98),rgba(15,10,5,0.99));border:2px solid #f0c64a;border-radius:14px;box-shadow:0 15px 45px rgba(0,0,0,0.85),0 0 25px rgba(240,198,74,0.25);overflow:hidden;display:flex;flex-direction:column;color:#f4ead4;">
        <div class="tourney-modal-header" style="display:flex;justify-content:space-between;align-items:center;padding:14px 20px;border-bottom:1px solid rgba(240,198,74,0.3);background:rgba(0,0,0,0.35);flex-wrap:wrap;gap:8px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <span style="font-size:1.6rem;">🏆</span>
            <div>
              <div style="font-size:1.15rem;font-weight:900;color:#f0c64a;letter-spacing:0.04em;">${state.title}</div>
              <div style="font-size:0.78rem;color:#d6d3d1;">
                ${state.playerCount} Players · ${formatLabel} · ${seriesLabel}
                ${state.isComplete ? '· <strong style="color:#34d399;">CHAMPION CROWNED</strong>' : ''}
              </div>
            </div>
          </div>
          <div style="display:flex;gap:8px;align-items:center;">
            <button type="button" class="btn-ghost" id="btnCopyTourneySummary" style="padding:6px 10px;font-size:0.8rem;border:1px solid rgba(240,198,74,0.4);border-radius:6px;color:#f0c64a;cursor:pointer;">
              📋 Share Recap
            </button>
            <button type="button" class="btn-ghost" id="btnNewTourney" style="padding:6px 10px;font-size:0.8rem;border:1px solid rgba(255,255,255,0.2);border-radius:6px;color:#d6d3d1;cursor:pointer;">
              ⚙ New Bracket
            </button>
            <button type="button" class="btn-ghost tourney-close-btn" id="btnTourneyCloseTop" style="background:transparent;border:none;color:#a8a29e;font-size:1.5rem;cursor:pointer;padding:4px 8px;line-height:1;">✕</button>
          </div>
        </div>

        <div class="tourney-modal-body" style="padding:18px 20px;overflow-x:auto;overflow-y:auto;flex:1;">
          ${podiumHtml}

          <div class="tourney-bracket-container" style="display:flex;align-items:stretch;min-width:max-content;padding-bottom:10px;">
            ${Object.keys(winnersRounds).map(rnd => {
              const rMatches = winnersRounds[rnd];
              const label = rnd === '1' && state.playerCount > 4 ? 'Quarterfinals' : (rnd === '1' ? 'Semifinals' : 'Semifinals');
              return renderRoundColumn(rMatches, label);
            }).join('')}

            ${finalsMatches.length > 0 ? renderRoundColumn(finalsMatches, finalsMatches[0].roundLabel || 'Finals') : ''}
          </div>

          ${state.format === 'double' && Object.keys(losersRounds).length > 0 ? `
            <div style="margin-top:20px;border-top:1px dashed rgba(240,198,74,0.3);padding-top:16px;">
              <div style="font-size:0.95rem;font-weight:900;color:#c084fc;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:12px;">
                ⚡ Losers / Consolation Bracket
              </div>
              <div class="tourney-bracket-container" style="display:flex;align-items:stretch;min-width:max-content;padding-bottom:10px;">
                ${Object.keys(losersRounds).map(rnd => {
                  const rMatches = losersRounds[rnd];
                  return renderRoundColumn(rMatches, `Losers Round ${rnd}`);
                }).join('')}
              </div>
            </div>
          ` : ''}
        </div>

        <div class="tourney-modal-footer" style="padding:12px 20px;border-top:1px solid rgba(240,198,74,0.3);background:rgba(0,0,0,0.4);display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
          <div style="font-size:0.82rem;color:#a8a29e;">
            💡 Play directly on the digital table or record physical cribbage board match results manually.
          </div>
          <button type="button" class="btn-ghost" id="btnTourneyCloseBottom" style="padding:8px 18px;border:1px solid rgba(255,255,255,0.2);border-radius:6px;color:#f4ead4;cursor:pointer;">
            Close to Table
          </button>
        </div>
      </div>
    `;

    // Wire buttons
    overlay.querySelectorAll('.tourney-close-btn, #btnTourneyCloseBottom, #btnTourneyCloseTop').forEach(btn => {
      btn.addEventListener('click', () => TournamentModal.close());
    });

    const copyBtn = overlay.querySelector('#btnCopyTourneySummary');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const text = TournamentManager.generateShareSummary(state);
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
          navigator.clipboard.writeText(text).then(() => {
            copyBtn.textContent = '✔ Copied!';
            setTimeout(() => { copyBtn.textContent = '📋 Share Recap'; }, 2000);
          }).catch(() => {});
        }
      });
    }

    const newBtn = overlay.querySelector('#btnNewTourney');
    if (newBtn) {
      newBtn.addEventListener('click', () => {
        TournamentModal.renderSetup(overlay, { ...context, forceSetup: true });
      });
    }

    // Play match buttons
    const playBtns = overlay.querySelectorAll('.btn-play-match');
    playBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const matchId = btn.getAttribute('data-match-id');
        TournamentModal.launchMatch(matchId, context);
      });
    });

    // Manual score record buttons
    const manualBtns = overlay.querySelectorAll('.btn-manual-score');
    manualBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const matchId = btn.getAttribute('data-match-id');
        TournamentModal.promptManualScore(matchId, context);
      });
    });

    // Simulate buttons
    const simBtns = overlay.querySelectorAll('.btn-sim-match');
    simBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const matchId = btn.getAttribute('data-match-id');
        const res = TournamentManager.simulateCpuMatch(matchId);
        if (res) {
          TournamentModal._broadcastTournament(res.state, context);
          TournamentModal.render(context);
        }
      });
    });
  }

  /**
   * Prompts a modal to enter manual match result (for pub game nights on physical boards).
   * @param {string} matchId
   * @param {Object} context
   */
  static promptManualScore(matchId, context = {}) {
    const state = TournamentManager.getTournamentState();
    if (!state || !state.matches || !state.matches[matchId]) return;

    const match = state.matches[matchId];
    const p1 = TournamentManager.getPlayer(state, match.player1Id);
    const p2 = TournamentManager.getPlayer(state, match.player2Id);
    if (!p1 || !p2) return;

    const modalManager = context.modalManager || (typeof window !== 'undefined' ? window.modalManager : null);
    const showModal = (typeof window !== 'undefined' && typeof window.showModal === 'function') ? window.showModal : null;

    const modalHtml = `
      <div class="manual-score-modal" style="padding:10px;text-align:center;">
        <h3 style="color:#f0c64a;font-size:1.2rem;margin-bottom:6px;">Record Match Result</h3>
        <p style="font-size:0.86rem;color:#d6d3d1;margin-bottom:16px;">
          Select the winner of <strong>${p1.name}</strong> vs <strong>${p2.name}</strong>:
        </p>

        <div style="display:flex;gap:12px;justify-content:center;margin-bottom:18px;">
          <button type="button" class="btn-gold" id="btnPickWinner1" style="flex:1;padding:12px;border-radius:8px;font-weight:900;font-size:1rem;cursor:pointer;">
            ${p1.avatar} ${p1.name} Won
          </button>
          <button type="button" class="btn-gold" id="btnPickWinner2" style="flex:1;padding:12px;border-radius:8px;font-weight:900;font-size:1rem;cursor:pointer;">
            ${p2.avatar} ${p2.name} Won
          </button>
        </div>

        <button type="button" class="btn-ghost modal-btn-close" id="btnCancelManualResult" style="padding:8px 16px;border:1px solid rgba(255,255,255,0.2);border-radius:6px;color:#d6d3d1;cursor:pointer;">
          Cancel
        </button>
      </div>
    `;

    if (modalManager && typeof modalManager.showModal === 'function') {
      modalManager.showModal(modalHtml, true);
    } else if (showModal) {
      showModal(modalHtml, true);
    }

    setTimeout(() => {
      const btn1 = document.getElementById('btnPickWinner1');
      const btn2 = document.getElementById('btnPickWinner2');
      const hide = (typeof window !== 'undefined' && typeof window.hideModal === 'function') ? window.hideModal : () => {};
      const btnCancel = document.getElementById('btnCancelManualResult');
      if (btnCancel) btnCancel.addEventListener('click', () => hide());

      if (btn1) {
        btn1.addEventListener('click', () => {
          const res = TournamentManager.manualSetMatchWinner(matchId, p1.id);
          hide();
          if (res) {
            TournamentModal._broadcastTournament(res.state, context);
            TournamentModal.render(context);
          }
        });
      }
      if (btn2) {
        btn2.addEventListener('click', () => {
          const res = TournamentManager.manualSetMatchWinner(matchId, p2.id);
          hide();
          if (res) {
            TournamentModal._broadcastTournament(res.state, context);
            TournamentModal.render(context);
          }
        });
      }
    }, 100);
  }

  /**
   * Launches active tournament match onto the digital cribbage table.
   * @param {string} matchId
   * @param {Object} context
   */
  static launchMatch(matchId, context = {}) {
    const match = TournamentManager.setActiveMatch(matchId);
    if (!match) return;

    const state = TournamentManager.getTournamentState();
    const p1 = TournamentManager.getPlayer(state, match.player1Id);
    const p2 = TournamentManager.getPlayer(state, match.player2Id);
    if (!p1 || !p2) return;

    TournamentModal.close();

    // In Crazy Cribbage, Seat 1 ('p1'/'you') is the bottom player on screen.
    // If one is human and one is CPU, the human must ALWAYS occupy Seat 1.
    let seat1Player = p1;
    let seat2Player = p2;

    if (p1.type === 'cpu' && p2.type === 'human') {
      seat1Player = p2;
      seat2Player = p1;
    }

    match.seatMap = {
      p1: seat1Player.id,
      p2: seat2Player.id
    };
    TournamentManager.saveTournamentState(state);

    const isHumanVsHuman = seat1Player.type === 'human' && seat2Player.type === 'human';
    const isOnline = seat1Player.type === 'online' || seat2Player.type === 'online';
    const oppMode = isHumanVsHuman ? 'human' : (isOnline ? 'online' : 'cpu');
    const aiProf = (seat2Player.type === 'cpu' ? (seat2Player.cpuProfile || 'shark') : 'shark');

    // Synchronize HTML form controls so LobbyController doesn't revert to old form states
    if (typeof document !== 'undefined') {
      const optPlayerCount = document.getElementById('optPlayerCount');
      if (optPlayerCount) optPlayerCount.value = '2';

      const optOpp = document.getElementById('optOpp');
      if (optOpp) optOpp.value = oppMode;

      const optAiProfile = document.getElementById('optAiProfile');
      if (optAiProfile) optAiProfile.value = aiProf;

      const optSeat2Type = document.getElementById('optSeat2Type');
      if (optSeat2Type) optSeat2Type.value = (oppMode === 'human' ? 'human' : `cpu:${aiProf}`);

      const modePills = document.querySelectorAll('.game-mode-pill');
      modePills.forEach(p => p.classList.toggle('active', p.dataset.mode === oppMode));
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      settings.playerCount = 2;
      settings.gameMode = 'individual';
      settings.opp = oppMode;
      settings.aiProfile = aiProf;
      settings.isTournamentMatch = true;

      // Ensure seats is a valid Array
      settings.seats = [
        {
          id: 'p1',
          name: seat1Player.name,
          type: seat1Player.type === 'online' ? 'online' : (seat1Player.type === 'human' ? 'human' : 'cpu'),
          personality: seat1Player.cpuProfile || 'shark',
          team: 1,
          color: '#d4a017'
        },
        {
          id: 'p2',
          name: seat2Player.name,
          type: seat2Player.type === 'online' ? 'online' : (seat2Player.type === 'human' ? 'human' : 'cpu'),
          personality: aiProf,
          team: 2,
          color: '#ef4444'
        }
      ];
    }

    // Launch game via GameEngine / app
    const startGame = context.startGame || (typeof window !== 'undefined' ? window.startGame : null);
    if (typeof startGame === 'function') {
      startGame();
    }
  }

  /**
   * Broadcasts tournament update over WebRTC to multiplayer room.
   */
  static _broadcastTournament(tourneyState, context = {}) {
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !net.clients || typeof net.broadcast !== 'function' && !net.clients.size) return;

    const payload = {
      type: 'tournament_update',
      tournament: tourneyState
    };

    if (typeof net.broadcast === 'function') {
      net.broadcast(payload);
    } else {
      for (const client of net.clients.values()) {
        if (client.conn && client.conn.open) {
          try { client.conn.send(payload); } catch (e) {}
        }
      }
    }
  }
}
